import { Table, Tag, Button, Space } from '@/shared/ui/primitives';
import { EditOutlined, DeleteOutlined } from '@/shared/ui/icons';
import { formatDateTime } from '@/shared/utils/formatters';
import { useTranslation } from 'react-i18next';
import type { ColumnsType, TableProps } from '@/shared/ui/subpath-types';

const TYPE_COLORS: Record<string, string> = {
  MESSAGE: 'blue',
  AD_RESPONSE: 'green',
  CHAT: 'purple',
  PRICE_CHANGE: 'gold',
  VERIFICATION: 'purple',
  AD_EXPIRING: 'orange',
  SYSTEM: 'default',
};

interface Props {
  data: any[];
  loading: boolean;
  sortField: string;
  sortDir: string;
  onSort: (field: string) => void;
  onEdit: (id: number) => void;
  onDelete: (item: any) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

export default function NotificationTable({ data, loading, sortField, sortDir, onSort, onEdit, onDelete, page, pageSize, total, onPageChange }: Props) {
  const { t } = useTranslation();

  const columns: ColumnsType<any> = [
    { title: t('common.id'), dataIndex: 'id', sorter: true, width: 80 },
    {
      title: t('common.user'), dataIndex: 'userId', sorter: true,
      render: (_: any, record: any) => (
        <>
          <span style={{ fontWeight: 500 }}>{record.userName || '—'}</span>
          <span style={{ color: '#999', marginLeft: 4 }}>(#{record.userId})</span>
        </>
      ),
    },
    {
      title: t('common.type'), dataIndex: 'type', sorter: true, width: 130,
      render: (v: string) => <Tag color={TYPE_COLORS[v] || 'default'}>{v}</Tag>,
    },
    {
      title: t('notifications.titleEn'), dataIndex: 'titleEn', sorter: true, ellipsis: true,
      render: (v: string) => v || '—',
    },
    {
      title: t('filters.readStatus'), dataIndex: 'isRead', sorter: true, width: 100,
      render: (v: boolean) => <Tag color={v ? 'success' : 'warning'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('common.createdAt'), dataIndex: 'createdAt', sorter: true,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: t('common.actions'), width: 100,
      render: (_: any, record: any) => (
        <Space>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(record.id)} />
          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(record)} />
        </Space>
      ),
    },
  ];

  const handleChange: TableProps['onChange'] = (_pagination, _filters, sorter: any) => {
    if (sorter?.field) onSort(sorter.field);
  };

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      onChange={handleChange}
      sortDirections={['ascend', 'descend']}
      showSorterTooltip={false}
      pagination={{
        current: page + 1,
        pageSize,
        total,
        showTotal: (total) => `${total} items`,
        showSizeChanger: false,
        onChange: (p) => onPageChange(p - 1),
      }}
    />
  );
}
